import { useState, useEffect } from "react";
import { projectAuth } from "../firebase/config";
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";

export const useUpdatePassword = () => {
  const [isCancelled, setIsCancelled] = useState(false);
  const [error, setError] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const [success, setSuccess] = useState(false);

  const changePassword = async (currentPassword, newPassword) => {
    setError(null);
    setIsPending(true);
    setSuccess(false);

    try {
      const user = projectAuth.currentUser;

      if (!user || !user.email) {
        throw new Error("No user is currently logged in");
      }

      // Re-authenticate the user with their current password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update to the new password
      await updatePassword(user, newPassword);

      if (!isCancelled) {
        setIsPending(false);
        setSuccess(true);
        setError(null);
        return true;
      }
    } catch (err) {
      if (!isCancelled) {
        // Provide user-friendly error messages
        let errorMessage = err.message;

        if (err.code === "auth/wrong-password") {
          errorMessage = "Current password is incorrect";
        } else if (err.code === "auth/weak-password") {
          errorMessage = "New password is too weak. Please use at least 6 characters";
        } else if (err.code === "auth/requires-recent-login") {
          errorMessage = "Please log out and log back in, then try again";
        } else if (err.code) {
          errorMessage = err.code.replace("auth/", "").replace(/-/g, " ");
        }

        setError(errorMessage);
        setIsPending(false);
        return false;
      }
    }
  };

  const resetState = () => {
    setError(null);
    setSuccess(false);
  };

  useEffect(() => {
    return () => setIsCancelled(true);
  }, []);

  return { changePassword, isPending, error, success, resetState };
};
